import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  InputAdornment,
  CircularProgress
} from '@mui/material';
import { FaEye, FaEyeSlash, FaKey, FaEnvelope } from 'react-icons/fa';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const DevModeCredentialsModal = ({ open, onSubmit, loading = false }) => {
  const [apiKey, setApiKey] = useState('');
  const [email, setEmail] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);
  const [errors, setErrors] = useState({ apiKey: '', email: '' });

  const validateApiKey = (value) => {
    if (!value) {
      return 'API Key is required';
    }
    if (value.length < 10) {
      return 'API Key must be at least 10 characters';
    }
    return '';
  };

  const validateEmail = (value) => {
    if (!value) {
      return 'Email is required';
    }
    if (!EMAIL_REGEX.test(value)) {
      return 'Please enter a valid email address';
    }
    return '';
  };

  const handleApiKeyChange = (e) => {
    const value = e.target.value;
    setApiKey(value);
    setErrors(prev => ({ ...prev, apiKey: validateApiKey(value) }));
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    setErrors(prev => ({ ...prev, email: validateEmail(value) }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const apiKeyError = validateApiKey(apiKey);
    const emailError = validateEmail(email);

    setErrors({ apiKey: apiKeyError, email: emailError });

    if (!apiKeyError && !emailError) {
      onSubmit(apiKey, email);
    }
  };

  const isFormValid = apiKey && email && !errors.apiKey && !errors.email;

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
      onClose={(event, reason) => {
        if (reason === 'backdropClick') {
          return false;
        }
      }}
    >
      <DialogTitle>
        <Typography variant="h5" component="div" fontWeight="bold">
          Dev Mode Configuration
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Please enter your API credentials to continue
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
          <TextField
            fullWidth
            label="API Key"
            type={showApiKey ? 'text' : 'password'}
            value={apiKey}
            onChange={handleApiKeyChange}
            error={Boolean(errors.apiKey)}
            helperText={errors.apiKey}
            disabled={loading}
            sx={{ mb: 3 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FaKey style={{ color: '#757575' }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowApiKey(!showApiKey)}
                    edge="end"
                    disabled={loading}
                  >
                    {showApiKey ? <FaEyeSlash /> : <FaEye />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />

          <TextField
            fullWidth
            label="Email Address"
            type="email"
            value={email}
            onChange={handleEmailChange}
            error={Boolean(errors.email)}
            helperText={errors.email}
            disabled={loading}
            sx={{ mb: 3 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FaEnvelope style={{ color: '#757575' }} />
                </InputAdornment>
              )
            }}
          />

          <Button
            type="submit"
            variant="contained"
            fullWidth
            size="large"
            disabled={!isFormValid || loading}
            sx={{
              height: 48,
              position: 'relative'
            }}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Continue'
            )}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default DevModeCredentialsModal;
